// Typed fetch wrapper. Every call goes through here, so error handling, the
// session-expiry redirect and the request shape are defined in one place.

import type {
  BackupRun, BackupSettings, BackupSettingsInput,
  Client, ClientDetailReport, ClientReport, Currency, ExportPreview, Invoice, InvoiceInput,
  InvoiceStatus, InvoiceSummary, InvoiceTotals, Locale, MailSettings, MailSettingsInput, Page, PaymentMethod,
  Product, Quotation, QuotationInput,
  QuotationSummary, QuotationTotals, SendExportInput, Settings, Supplier, SupplierInvoice,
  SupplierInvoiceInput, SupplierInvoiceTotals, User,
  VerifactuQueue, VerifactuRecord, VerifactuRecordStatus, VerifactuSettings,
  VerifactuSettingsInput,
} from '@shared/types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    /** Field name -> already-translated message, for inline form errors. */
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Notified when the server reports the session is gone, so the app can reset. */
let onUnauthorized: (() => void) | undefined;
export const setUnauthorizedHandler = (fn: () => void) => { onUnauthorized = fn; };

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(body instanceof FormData || body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body instanceof FormData ? body : body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'error.network', 'error.network');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: unknown;
  try { payload = text ? JSON.parse(text) : undefined; } catch { payload = undefined; }

  if (!res.ok) {
    const err = (payload ?? {}) as { error?: string; code?: string; fields?: Record<string, string> };
    // 401 on any call but the login attempt means the session lapsed.
    if (res.status === 401 && !path.startsWith('/auth/login')) onUnauthorized?.();
    throw new ApiError(res.status, err.error ?? 'error.unexpected', err.code, err.fields);
  }

  return payload as T;
}

type QueryValue = string | number | boolean | undefined;

const qs = (params: Readonly<Record<string, QueryValue>> | object) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, QueryValue][]) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export interface ListParams {
  q?: string;
  page?: number;
  perPage?: number;
}

export interface SessionInfo {
  user: User;
  company: { name: string; logoPath: string };
  taxRate: number;
  currencyCode: string;
  defaultLocale: Locale;
}

export interface InvoicePage extends Page<InvoiceSummary> {
  totals: InvoiceTotals;
}

export interface InvoiceDefaults {
  issuedAt: string;
  sellerId: number | null;
  taxRate: number;
  currency: Currency;
}

export interface QuotationPage extends Page<QuotationSummary> {
  totals: QuotationTotals;
}

export interface QuotationDefaults extends InvoiceDefaults {
  validUntil: string;
}

/** Subject, body and recipient the compose dialog opens with. */
export interface EmailDefaults {
  to: string;
  subject: string;
  message: string;
  filename: string;
  /** False when the server has no SMTP configuration. */
  enabled: boolean;
}

/** The outcome of an AEAT connection test. A refusal is a result, not an error. */
export interface VerifactuTestResult {
  ok: boolean;
  endpoint?: string;
  /** What AEAT or the TLS layer said, in its own words. */
  detail?: string;
}

/** The outcome of a mail server test. Not an error: a refusal is a result. */
export interface MailTestResult {
  ok: boolean;
  to?: string;
  messageId?: string;
  /** Why the relay could not be used, in its own words. Absent when ok. */
  detail?: string;
}

/** What the server says must happen after a correct password. */
export interface LoginChallenge {
  stage: 'totp' | 'enrol';
  locale: Locale;
}

export interface EnrolmentOffer {
  secret: string;
  otpauthUri: string;
  /** data: URL of the QR image. */
  qr: string;
}

export interface TwoFactorStatus {
  enabled: boolean;
  confirmedAt: string | null;
  recoveryCodesRemaining: number;
}

export interface VerifiedSignIn {
  user: User;
  usedRecoveryCode: boolean;
  recoveryCodesRemaining?: number;
}

export interface SendEmailInput {
  to: string;
  cc?: string;
  subject: string;
  message: string;
}

export const api = {
  // ── auth ────────────────────────────────────────────────────────────────
  /** Step one. Never returns a session: two-step verification is required. */
  login: (username: string, password: string) =>
    request<LoginChallenge>('POST', '/auth/login', { username, password }),
  /** Step two for an enrolled account. Accepts a TOTP or a recovery code. */
  verifyTotp: (code: string) => request<VerifiedSignIn>('POST', '/auth/totp/verify', { code }),
  /** Step two for an account with no authenticator yet. */
  beginEnrolment: () => request<EnrolmentOffer>('POST', '/auth/totp/enrol'),
  confirmEnrolment: (code: string) =>
    request<{ user: User; recoveryCodes: string[] }>('POST', '/auth/totp/confirm', { code }),

  // ── managing your own second factor ─────────────────────────────────────
  securityStatus: () => request<TwoFactorStatus>('GET', '/auth/security'),
  reconfigureTotp: (password: string) =>
    request<EnrolmentOffer>('POST', '/auth/security/totp/reconfigure', { password }),
  confirmReconfigure: (code: string) =>
    request<{ recoveryCodes: string[] }>('POST', '/auth/security/totp/confirm', { code }),
  regenerateRecoveryCodes: (password: string) =>
    request<{ recoveryCodes: string[] }>('POST', '/auth/security/recovery-codes', { password }),
  resetUserTwoFactor: (id: number, password: string) =>
    request<{ reset: true; name: string }>('POST', `/users/${id}/reset-two-factor`, { password }),
  logout: () => request<void>('POST', '/auth/logout'),
  session: () => request<SessionInfo>('GET', '/auth/me'),

  // ── invoices ────────────────────────────────────────────────────────────
  invoices: (params: ListParams & { status?: string } = {}) =>
    request<InvoicePage>('GET', `/invoices${qs(params)}`),
  invoice: (id: number) => request<Invoice>('GET', `/invoices/${id}`),
  invoiceDefaults: () => request<InvoiceDefaults>('GET', '/invoices/new/defaults'),
  createInvoice: (input: InvoiceInput) => request<Invoice>('POST', '/invoices', input),
  updateInvoice: (id: number, input: InvoiceInput) => request<Invoice>('PUT', `/invoices/${id}`, input),
  setInvoiceStatus: (id: number, status: InvoiceStatus) =>
    request<{ number: number; status: InvoiceStatus }>('PATCH', `/invoices/${id}/status`, { status }),
  deleteInvoice: (id: number) => request<{ number: number }>('DELETE', `/invoices/${id}`),
  /** PDFs are fetched as a normal navigation so the browser handles the download. */
  invoicePdfUrl: (id: number, locale: Locale) => `/api/invoices/${id}/pdf?lang=${locale}`,

  /** Records that this document was emailed; shown in the list. */
  invoiceEmailDefaults: (id: number, locale: Locale) =>
    request<EmailDefaults>('GET', `/invoices/${id}/email${qs({ lang: locale })}`),
  emailInvoice: (id: number, input: SendEmailInput) =>
    request<{ sent: true; to: string[] }>('POST', `/invoices/${id}/email`, input),

  // ── quotations ──────────────────────────────────────────────────────────
  quotations: (params: ListParams & { status?: string } = {}) =>
    request<QuotationPage>('GET', `/quotations${qs(params)}`),
  quotation: (id: number) => request<Quotation>('GET', `/quotations/${id}`),
  quotationDefaults: () => request<QuotationDefaults>('GET', '/quotations/new/defaults'),
  createQuotation: (input: QuotationInput) => request<Quotation>('POST', '/quotations', input),
  updateQuotation: (id: number, input: QuotationInput) =>
    request<Quotation>('PUT', `/quotations/${id}`, input),
  deleteQuotation: (id: number) => request<{ number: number }>('DELETE', `/quotations/${id}`),
  convertQuotation: (id: number, paymentMethod: PaymentMethod) =>
    request<{ invoiceId: number; invoiceNumber: number }>(
      'POST', `/quotations/${id}/convert`, { paymentMethod }),
  quotationPdfUrl: (id: number, locale: Locale) => `/api/quotations/${id}/pdf?lang=${locale}`,
  quotationEmailDefaults: (id: number, locale: Locale) =>
    request<EmailDefaults>('GET', `/quotations/${id}/email${qs({ lang: locale })}`),
  emailQuotation: (id: number, input: SendEmailInput) =>
    request<{ sent: true; to: string[] }>('POST', `/quotations/${id}/email`, input),

  // ── clients ─────────────────────────────────────────────────────────────
  clients: (params: ListParams & { activeOnly?: boolean } = {}) =>
    request<Page<Client>>('GET', `/clients${qs(params)}`),
  client: (id: number) => request<Client>('GET', `/clients/${id}`),
  searchClients: (q: string) => request<Client[]>('GET', `/clients/search${qs({ q })}`),
  createClient: (input: Partial<Client>) => request<Client>('POST', '/clients', input),
  updateClient: (id: number, input: Partial<Client>) => request<Client>('PUT', `/clients/${id}`, input),
  deleteClient: (id: number) => request<void>('DELETE', `/clients/${id}`),

  // ── products ────────────────────────────────────────────────────────────
  products: (params: ListParams & { activeOnly?: boolean } = {}) =>
    request<Page<Product>>('GET', `/products${qs(params)}`),
  createProduct: (input: Partial<Product>) => request<Product>('POST', '/products', input),
  updateProduct: (id: number, input: Partial<Product>) => request<Product>('PUT', `/products/${id}`, input),
  deleteProduct: (id: number) => request<void>('DELETE', `/products/${id}`),

  // ── users ───────────────────────────────────────────────────────────────
  users: (params: ListParams = {}) =>
    request<Page<User & { invoiceCount: number; twoFactorEnabled: boolean }>>(
      'GET', `/users${qs(params)}`),
  activeUsers: () => request<User[]>('GET', '/users/active'),
  createUser: (input: Record<string, unknown>) => request<User>('POST', '/users', input),
  updateUser: (id: number, input: Record<string, unknown>) => request<User>('PUT', `/users/${id}`, input),
  setUserPassword: (id: number, password: string, passwordRepeat: string) =>
    request<void>('PUT', `/users/${id}/password`, { password, passwordRepeat }),
  setMyLocale: (locale: Locale) => request<{ locale: Locale }>('PUT', '/users/me/locale', { locale }),
  deleteUser: (id: number) => request<void>('DELETE', `/users/${id}`),

  // ── suppliers ───────────────────────────────────────────────────────────
  suppliers: (params: ListParams & { activeOnly?: boolean } = {}) =>
    request<Page<Supplier>>('GET', `/suppliers${qs(params)}`),
  supplier: (id: number) => request<Supplier>('GET', `/suppliers/${id}`),
  createSupplier: (input: Partial<Supplier>) => request<Supplier>('POST', '/suppliers', input),
  updateSupplier: (id: number, input: Partial<Supplier>) =>
    request<Supplier>('PUT', `/suppliers/${id}`, input),
  deleteSupplier: (id: number) => request<void>('DELETE', `/suppliers/${id}`),

  // ── purchase invoices ───────────────────────────────────────────────────
  purchases: (params: ListParams & {
    status?: string; supplierId?: number; from?: string; to?: string;
  } = {}) =>
    request<Page<SupplierInvoice> & { totals: SupplierInvoiceTotals }>(
      'GET', `/purchases${qs(params)}`),
  purchase: (id: number) => request<SupplierInvoice>('GET', `/purchases/${id}`),
  createPurchase: (input: SupplierInvoiceInput) =>
    request<SupplierInvoice>('POST', '/purchases', input),
  updatePurchase: (id: number, input: SupplierInvoiceInput) =>
    request<SupplierInvoice>('PUT', `/purchases/${id}`, input),
  deletePurchase: (id: number) => request<void>('DELETE', `/purchases/${id}`),
  uploadPurchaseFile: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<SupplierInvoice>('POST', `/purchases/${id}/file`, form);
  },
  deletePurchaseFile: (id: number) => request<SupplierInvoice>('DELETE', `/purchases/${id}/file`),
  purchaseFileUrl: (id: number) => `/api/purchases/${id}/file`,

  // ── accounting export ───────────────────────────────────────────────────
  exportCandidates: (from: string, to: string, onlyUnsent: boolean) =>
    request<ExportPreview>('GET', `/accounting/candidates${qs({ from, to, onlyUnsent })}`),
  exportDefaults: (from: string, to: string, locale: Locale) =>
    request<{ to: string; accountantName: string; subject: string; message: string; enabled: boolean }>(
      'GET', `/accounting/defaults${qs({ from, to, lang: locale })}`),
  sendExport: (from: string, to: string, locale: Locale, input: SendExportInput) =>
    request<{
      sent: true; to: string; count: number; salesCount: number;
      purchasesCount: number; missingFiles: number; archiveBytes: number;
    }>('POST', `/accounting/send${qs({ from, to, lang: locale })}`, input),
  exportArchiveUrl: (from: string, to: string, locale: Locale) =>
    `/api/accounting/archive${qs({ from, to, lang: locale })}`,

  // ── reports ─────────────────────────────────────────────────────────────
  clientReport: (year: number | null) =>
    request<ClientReport>('GET', `/reports/clients${qs({ year: year ?? '' })}`),
  clientDetailReport: (id: number) =>
    request<ClientDetailReport>('GET', `/reports/clients/${id}`),
  clientReportCsvUrl: (year: number | null, locale: Locale) =>
    `/api/reports/clients.csv${qs({ year: year ?? '', lang: locale })}`,

  // ── backups ─────────────────────────────────────────────────────────────
  backupSettings: () => request<BackupSettings>('GET', '/backups'),
  updateBackupSettings: (input: BackupSettingsInput) =>
    request<BackupSettings>('PUT', '/backups', input),
  backupRuns: () => request<BackupRun[]>('GET', '/backups/runs'),
  testBackupDestination: () => request<{ ok: true; detail: string }>('POST', '/backups/test'),
  runBackup: () => request<BackupRun>('POST', '/backups/run'),
  backupDownloadUrl: () => '/api/backups/download',

  // ── settings ────────────────────────────────────────────────────────────
  settings: () => request<Settings>('GET', '/settings'),
  updateSettings: (input: Partial<Settings>) => request<Settings>('PUT', '/settings', input),
  currencies: () => request<Currency[]>('GET', '/settings/currencies'),
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('logo', file);
    return request<Settings>('POST', '/settings/logo', form);
  },
  deleteLogo: () => request<Settings>('DELETE', '/settings/logo'),

  // ── mail server ─────────────────────────────────────────────────────────
  mailSettings: () => request<MailSettings>('GET', '/settings/mail'),
  updateMailSettings: (input: MailSettingsInput) =>
    request<MailSettings>('PUT', '/settings/mail', input),
  /**
   * Resolves even when the relay refused: a rejection is an answer about the
   * configuration, and `detail` is the relay's own words about what is wrong.
   */
  testMailServer: (to: string) =>
    request<MailTestResult>('POST', '/settings/mail/test', { to }),

  // ── VERI*FACTU ──────────────────────────────────────────────────────────
  verifactuSettings: () => request<VerifactuSettings>('GET', '/verifactu/settings'),
  updateVerifactuSettings: (input: VerifactuSettingsInput) =>
    request<VerifactuSettings>('PUT', '/verifactu/settings', input),
  /** The file and its password go together: neither is stored without the other. */
  uploadVerifactuCertificate: (file: File, password: string) => {
    const form = new FormData();
    form.append('certificate', file);
    form.append('password', password);
    return request<VerifactuSettings>('POST', '/verifactu/certificate', form);
  },
  deleteVerifactuCertificate: () =>
    request<VerifactuSettings>('DELETE', '/verifactu/certificate'),
  /**
   * Resolves even when AEAT refused: `detail` is what AEAT or the TLS layer
   * actually said, which is the only text that says what to fix.
   */
  testVerifactu: () =>
    request<VerifactuTestResult>('POST', '/verifactu/test'),

  verifactuQueue: () => request<VerifactuQueue>('GET', '/verifactu/queue'),
  verifactuRecords: (params: { status?: VerifactuRecordStatus; limit?: number } = {}) =>
    request<VerifactuRecord[]>('GET', `/verifactu/records${qs(params)}`),
  sendVerifactuRecord: (id: number) =>
    request<VerifactuRecord>('POST', `/verifactu/records/${id}/send`),
  invoiceVerifactuRecords: (invoiceId: number) =>
    request<VerifactuRecord[]>('GET', `/verifactu/invoices/${invoiceId}/records`),
  fileInvoice: (invoiceId: number) =>
    request<VerifactuRecord>('POST', `/verifactu/invoices/${invoiceId}/register`),
  withdrawInvoice: (invoiceId: number) =>
    request<VerifactuRecord>('POST', `/verifactu/invoices/${invoiceId}/cancel`),
};
